import { AxiosResponse } from "axios";
import { Channel } from "redux-saga";
import { actionChannel, call, fork, put, take, takeLatest } from "redux-saga/effects";
import {
    finishAppleSetup,
    getAppleSetup as getAppleSetupRequest,
    patchAppleSetupField,
    uploadAppleSetupKey,
} from "../../services/apple-setup/apple-setup.service";
import {
    finish,
    finishFailure,
    finishSuccess,
    getAppleSetup,
    getAppleSetupFailure,
    getAppleSetupSuccess,
    saveField,
    saveFieldFailure,
    saveFieldSuccess,
    uploadKey,
    uploadKeyFailure,
    uploadKeySuccess,
} from "../reducer/apple-setup";
import {
    SaveFieldActionPayload,
    UploadKeyActionPayload,
} from "../types/apple-setup";

function* getAppleSetupSaga() {
    try {
        const { data }: AxiosResponse = yield call(getAppleSetupRequest);
        yield put(getAppleSetupSuccess(data.attributes));
    } catch (error: any) {
        yield put(getAppleSetupFailure({ forbidden: error?.response?.status === 403 }));
    }
}

function* saveFieldSaga(action: SaveFieldActionPayload) {
    const { field, value } = action.payload;
    try {
        const { data }: AxiosResponse = yield call(patchAppleSetupField, field, value);
        yield put(saveFieldSuccess({ field, attributes: data.attributes }));
    } catch (error: any) {
        const fieldErrors: string[] = error?.response?.data?.errors?.[field] ?? [];
        yield put(saveFieldFailure({ field, errors: fieldErrors }));
    }
}

// Field saves PATCH the same URL (/api/v1/apple-setup) for every field. The
// shared httpClient aborts any in-flight request to a URL when another starts,
// and aborted requests never settle — so concurrent saves would leave fields
// stuck "saving" and drop earlier saves. Serialize them: one PATCH at a time.
function* watchSaveFieldSaga() {
    const channel: Channel<SaveFieldActionPayload> = yield actionChannel(saveField.type);
    while (true) {
        const action: SaveFieldActionPayload = yield take(channel);
        yield call(saveFieldSaga, action);
    }
}

function* uploadKeySaga(action: UploadKeyActionPayload) {
    try {
        const { data }: AxiosResponse = yield call(uploadAppleSetupKey, action.payload);
        yield put(uploadKeySuccess(data.attributes));
    } catch (_error: unknown) {
        yield put(uploadKeyFailure());
    }
}

function* finishSaga() {
    try {
        const { data }: AxiosResponse = yield call(finishAppleSetup);
        yield put(finishSuccess(data.attributes));
    } catch (_error: unknown) {
        yield put(finishFailure());
    }
}

export function* rootSaga() {
    yield takeLatest(getAppleSetup.type, getAppleSetupSaga);
    yield fork(watchSaveFieldSaga);
    yield takeLatest(uploadKey.type, uploadKeySaga);
    yield takeLatest(finish.type, finishSaga);
}
